import { useQueryClient } from '@tanstack/react-query';
const PREFIX = ['platform', 'integration'];
export function createIntegrationRepository(config) {
    const { repository, client } = config;
    function getFindQueryKey(props) {
        return [...PREFIX, 'find', props.workspace, props.definition, props.limit];
    }
    function getRetrieveQueryKey(integrationId) {
        return [...PREFIX, 'retrieve', integrationId];
    }
    function getAllQueryKeys() {
        return [...PREFIX];
    }
    return {
        useFind(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getFindQueryKey(props),
                async queryFn() {
                    const response = await client.user.integrationList(props);
                    return response.data.data;
                },
            });
            return query;
        },
        useRetrieve(context, integrationId, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getRetrieveQueryKey(integrationId),
                async queryFn() {
                    const response = await client.user.integrationRetrieve({
                        integrationId,
                    });
                    return response.data;
                },
            });
            return query;
        },
        useCreate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(payload) {
                    const response = await client.user.integrationCreate({
                        integrationCreatePayload: payload,
                    });
                    return response.data;
                },
                onSuccess(data, variables) {
                    // Invalidate all integration queries to refresh lists
                    queryClient.invalidateQueries({ queryKey: getAllQueryKeys() });
                },
            });
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn({ integrationId, payload }) {
                    const response = await client.user.integrationUpdate({
                        integrationId,
                        integrationUpdatePayload: payload,
                    });
                    return response.data;
                },
                onSuccess(data, variables) {
                    // Update the specific integration cache
                    const retrieveKey = getRetrieveQueryKey(variables.integrationId);
                    queryClient.setQueryData(retrieveKey, data);
                    // Invalidate list queries to refresh
                    queryClient.invalidateQueries({ queryKey: getAllQueryKeys() });
                },
            });
        },
        useUpsert(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(payload) {
                    // First, try to find existing integration with same workspace_id and definition_id
                    const existingResponse = await client.user.integrationList({
                        workspace: payload.workspace_id,
                        definition: payload.definition_id,
                        limit: 1,
                    });
                    const existingIntegration = existingResponse.data.data[0];
                    if (existingIntegration) {
                        // Integration exists, update it (only configuration can be updated)
                        const updateResponse = await client.user.integrationUpdate({
                            integrationId: existingIntegration.id,
                            integrationUpdatePayload: {
                                configuration: payload.configuration,
                            },
                        });
                        return updateResponse.data;
                    }
                    else {
                        // Integration doesn't exist, create it
                        const createResponse = await client.user.integrationCreate({
                            integrationCreatePayload: payload,
                        });
                        return createResponse.data;
                    }
                },
                onSuccess(data, variables) {
                    // Update the specific integration cache if we have the ID
                    if (data.id) {
                        const retrieveKey = getRetrieveQueryKey(data.id);
                        queryClient.setQueryData(retrieveKey, data);
                    }
                    // Invalidate all integration queries to refresh lists
                    queryClient.invalidateQueries({ queryKey: getAllQueryKeys() });
                },
            });
        },
        useDelete(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(integrationId) {
                    const response = await client.user.integrationDelete({
                        integrationId,
                    });
                    return response.data;
                },
                onSuccess(data, integrationId) {
                    // Remove the specific integration from cache
                    queryClient.removeQueries({
                        queryKey: getRetrieveQueryKey(integrationId),
                    });
                    // Invalidate all integration queries to refresh lists
                    queryClient.invalidateQueries({ queryKey: getAllQueryKeys() });
                },
            });
        },
    };
}
//# sourceMappingURL=integrationRepository.js.map